/**
 * Document links
 *
 * Scans the file for clickable links:
 *   - HTTP/HTTPS URLs anywhere inside STRING tokens (not just whole-string
 *     URLs — descriptions often embed a URL mid-text)
 *   - HTTP/HTTPS URLs inside line/block comments
 *
 * Each match becomes a `DocumentLink` with the surrounding character range
 * computed from the embedding token's source span.
 */

import type { DocumentLink, Position, Range } from "vscode-languageserver";
import type { FileState } from "../workspace/workspace.js";

const URL_REGEX = /https?:\/\/[^\s"'`<>)\]}]+/g;

export function provideDocumentLinks(file: FileState): DocumentLink[] {
  const out: DocumentLink[] = [];

  // Strings: scan inside the quoted content (excluding the quotes).
  for (const tok of file.tokens) {
    if (tok.type !== "STRING") continue;
    // The token's `value` is the unescaped content; the `span` covers the
    // quoted form in source. To map regex match offsets back into source,
    // we re-scan the source slice (between the quotes) directly.
    const startSrc = tok.span.start.offset + 1; // skip leading quote
    const endSrc = tok.span.end.offset - 1; // skip trailing quote
    const slice = file.text.slice(startSrc, endSrc);
    for (const link of findLinksIn(file.text, slice, startSrc)) {
      out.push(link);
    }
  }

  // Comments: walk leading + trailing trivia.
  const seenOffsets = new Set<number>();
  for (const tok of file.tokens) {
    for (const tr of [...tok.leadingTrivia, ...tok.trailingTrivia]) {
      if (tr.kind !== "lineComment" && tr.kind !== "blockComment") continue;
      if (seenOffsets.has(tr.span.start.offset)) continue;
      seenOffsets.add(tr.span.start.offset);
      const startSrc = tr.span.start.offset;
      const slice = tr.text;
      for (const link of findLinksIn(file.text, slice, startSrc)) {
        out.push(link);
      }
    }
  }

  return out;
}

function findLinksIn(
  fullText: string,
  slice: string,
  sliceStartOffset: number
): DocumentLink[] {
  const out: DocumentLink[] = [];
  for (const match of slice.matchAll(URL_REGEX)) {
    const url = match[0];
    if (!url) continue;
    const startOff = sliceStartOffset + (match.index ?? 0);
    const endOff = startOff + url.length;
    const range: Range = {
      start: positionAt(fullText, startOff),
      end: positionAt(fullText, endOff),
    };
    out.push({ range, target: url });
  }
  return out;
}

function positionAt(text: string, offset: number): Position {
  const o = Math.min(Math.max(offset, 0), text.length);
  let line = 0;
  let lineStart = 0;
  for (let i = 0; i < o; i++) {
    if (text[i] === "\n") {
      line++;
      lineStart = i + 1;
    }
  }
  return { line, character: o - lineStart };
}
